import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Modal } from '@calm-web/design-system';

import PaymentElementsForm from '@/components/purchase/PaymentElementsForm';
import PurchaseDetails from '@/components/purchase/PurchaseDetails';
import PurchaseForm from '@/components/purchase/PurchaseForm';
import { useStripeElements } from '@/components/purchase/StripeElementsProvider';
import { useAnalytics } from '@/hooks/analytics';
import { useSubPurchase } from '@/hooks/purchase/useSubPurchase';
import { useUserState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { useRouterPush } from '@/hooks/utils/useRouterPush';

import messages from './messages';
import {
	ConfirmButton,
	Copy,
	Cta,
	Divider,
	Heading,
	HeadingContainer,
	OrderSummary,
	PaymentTitleHeading,
	PaymentTitleWrapper,
	Subheading,
} from './styles';

const OnboardingPaymentForm = () => {
	const user = useUserState();
	const routerPush = useRouterPush();
	const { formatMessage } = useIntl();
	const { isStripeNextEnabled } = useStripeElements();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [showOrderSummary] = useState(!user?.subscription?.valid);
	const { logEvent } = useAnalytics();

	const submitSubPurchase = useSubPurchase();

	useComponentDidMount(() => {
		logEvent({
			eventName: 'Subscribe : Purchase : Form : Landed',
			eventProps: {
				is_stripe_next: isStripeNextEnabled,
			},
		});
	});

	function onTooltipClick(): void {
		setIsModalOpen(true);
		logEvent({
			eventName: 'Subscribe : Purchase : Form : Tool Tip : Clicked',
			eventProps: {
				tool_tip: 'why now',
			},
		});
	}

	async function onSuccess() {
		await routerPush('/premium-confirmation');
	}

	return (
		<>
			{showOrderSummary && (
				<>
					<HeadingContainer>
						<Heading>
							<FormattedMessage
								id="dodo.purchaseForm.heading"
								defaultMessage="Start Your Free 30-Day Trial"
								description="Signup flow payment title"
							/>
						</Heading>
						<Subheading>
							<FormattedMessage
								id="discountSignup.purchaseForm.preheading"
								defaultMessage="No commitment. Cancel anytime."
								description="Signup flow payment subtitle"
							/>
						</Subheading>
					</HeadingContainer>
					<Divider />
					<OrderSummary>
						<PurchaseDetails />
					</OrderSummary>
					<OrderSummary>
						<Divider />
						<PaymentTitleWrapper>
							<PaymentTitleHeading>{formatMessage(messages.heading)}</PaymentTitleHeading>
							<Cta onClick={onTooltipClick}>{formatMessage(messages.modalCta)}</Cta>
						</PaymentTitleWrapper>
						<Modal
							isOpen={isModalOpen}
							closeModal={(): void => setIsModalOpen(false)}
							canClose
							aria-label={formatMessage(messages.modalTitle)}
						>
							<Copy>{formatMessage(messages.trial)}</Copy>
							<Copy>{formatMessage(messages.cancel)}</Copy>
							<ConfirmButton backgroundColor="buttonLime" onPress={(): void => setIsModalOpen(false)}>
								{formatMessage(messages.confirmButtonCta)}
							</ConfirmButton>
						</Modal>
					</OrderSummary>
				</>
			)}
			{isStripeNextEnabled ? (
				<PaymentElementsForm
					endpoint="subscription"
					hidePriceAfterDisclaimer
					afterPurchaseSuccess={onSuccess}
					ctaCopy={formatMessage(messages.buttonCta)}
				/>
			) : (
				<PurchaseForm
					hidePriceAfterDisclaimer
					ctaCopy={formatMessage(messages.buttonCta)}
					onSuccess={onSuccess}
					onPurchase={submitSubPurchase}
				/>
			)}
		</>
	);
};

export default OnboardingPaymentForm;
